import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpTag, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ErrorAlert } from '../../components/error-alert';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';
import { useLeadErrorText } from './error-text';
import { isOwnerInactive, isProvider, RULE_STATUS_TONE, ruleStatus } from './labels';
import { invalidateLeads, type CatalogRow, type RuleView } from './queries';

/** Owners in round-robin order; an owner Pipedrive deactivated is a red chip (also the market defaults card). */
export function OwnerChips({ rule, activeIds }: { rule: RuleView; activeIds: ReadonlySet<number> | null }) {
  const { t } = useTranslation(['leads']);
  if (rule.owners.length === 0) return <span className="bo-muted">{t('rules.marketPool')}</span>;
  return (
    <span className="bo-chips">
      {rule.owners.map((o, i) => (
        <HpTag
          key={o.userPdId}
          tone={isOwnerInactive(o, activeIds) ? 'danger' : 'neutral'}
          title={isOwnerInactive(o, activeIds) ? t('rules.ownerInactiveTitle') : undefined}
        >
          {t('rules.ownerChip', { n: i + 1, name: o.name ?? t('rules.unknownId', { id: o.userPdId }) })}
        </HpTag>
      ))}
    </span>
  );
}

/** One ad campaign per row: its rule (or the lack of one), the leads it brought in 30 days and the actions. */
export function RulesTable({
  rows,
  canManage,
  activeIds,
  onEdit,
}: {
  rows: CatalogRow[];
  canManage: boolean;
  /** Active Pipedrive users once loaded; null while loading. */
  activeIds: ReadonlySet<number> | null;
  /** Opens the editor: for the row's rule, or a new rule for this campaign. */
  onEdit: (row: CatalogRow) => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const [removing, setRemoving] = useState<CatalogRow | null>(null);
  const setActive = useMutation(trpc.leads.rules.setActive.mutationOptions({ onSuccess: invalidateLeads }));
  const remove = useMutation(
    trpc.leads.rules.remove.mutationOptions({
      onSuccess: () => {
        setRemoving(null);
        return invalidateLeads();
      },
      // Close the modal on failure too, so the worded error above the table is not hidden behind the backdrop.
      onError: () => {
        setRemoving(null);
        return invalidateLeads();
      },
    }),
  );
  const columns = canManage ? 8 : 7;

  return (
    <>
      <ErrorAlert error={setActive.error ?? remove.error} describe={describe} className="bo-alert--inset" />
      <TableScroll label={t('rules.title')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('rules.columns.campaign')}</th>
            <th>{t('rules.columns.owners')}</th>
            <th>{t('rules.columns.target')}</th>
            <th>{t('rules.columns.campaignOption')}</th>
            <th>{t('rules.columns.exceptions')}</th>
            <th>{t('rules.columns.status')}</th>
            <th className="bo-num">{t('rules.columns.leads30d')}</th>
            {canManage && <th>{t('rules.columns.actions')}</th>}
          </BrutTheadRow>
          <tbody>
            {rows.map((row) => {
              const { campaign, rule } = row;
              const status = ruleStatus(row, activeIds);
              const busy = (id: string) =>
                (setActive.isPending && setActive.variables?.id === id) ||
                (remove.isPending && remove.variables?.id === id);
              return (
                <tr key={campaign.id}>
                  <td>
                    <span className="bo-row">
                      <HpTag tone="info">
                        {isProvider(campaign.provider) ? t(`provider.${campaign.provider}`) : campaign.provider}
                      </HpTag>
                      <span className="bo-strong">{campaign.name}</span>
                    </span>
                    <br />
                    <small className="bo-muted">
                      {[campaign.accountName, campaign.externalId, campaign.status].filter(Boolean).join(' · ')}
                    </small>
                  </td>
                  <td>
                    {rule ? <OwnerChips rule={rule} activeIds={activeIds} /> : <span className="bo-muted">—</span>}
                  </td>
                  <td>
                    {rule?.pipeline ? (
                      <span>
                        {rule.pipeline.name ?? t('rules.unknownId', { id: rule.pipeline.pdId })}
                        {rule.stage && (
                          <>
                            {' → '}
                            {rule.stage.name ?? t('rules.unknownId', { id: rule.stage.pdId })}
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="bo-muted">—</span>
                    )}
                  </td>
                  <td>
                    {rule?.campaignOption ? (
                      (rule.campaignOption.label ?? t('rules.unknownId', { id: rule.campaignOption.id }))
                    ) : (
                      <span className="bo-muted">—</span>
                    )}
                  </td>
                  <td>
                    {rule?.respectExistingOwner && (
                      <HpTag tone="neutral" title={t('rules.existingOwnerTitle')}>
                        {t('rules.existingOwner')}
                      </HpTag>
                    )}
                  </td>
                  <td>
                    <HpTag tone={RULE_STATUS_TONE[status]}>{t(`rules.status.${status}`)}</HpTag>
                  </td>
                  <td className="bo-num">
                    <span className="bo-row bo-row--end">
                      {fmtInt(row.leads30d)}
                      {row.leadsDefault30d > 0 && (
                        <HpTag tone="warning" title={t('rules.byDefaultTitle')}>
                          {t('rules.byDefault', { count: row.leadsDefault30d })}
                        </HpTag>
                      )}
                    </span>
                  </td>
                  {canManage && (
                    <td>
                      <span className="bo-row bo-nowrap">
                        <HpButton
                          variant="quiet"
                          icon={rule ? 'edit' : 'plus'}
                          className="bo-button--sm"
                          onClick={() => onEdit(row)}
                        >
                          {rule ? t('common:actions.edit') : t('rules.createRule')}
                        </HpButton>
                        {rule && (
                          <HpButton
                            variant="quiet"
                            icon={rule.active ? 'pause' : 'play'}
                            className="bo-button--sm"
                            busy={setActive.isPending && setActive.variables?.id === rule.id}
                            disabled={busy(rule.id)}
                            onClick={() => setActive.mutate({ id: rule.id, active: !rule.active })}
                          >
                            {rule.active ? t('common:actions.pause') : t('rules.resume')}
                          </HpButton>
                        )}
                        {rule && (
                          <HpButton
                            variant="quiet"
                            icon="trash"
                            className="bo-button--sm"
                            disabled={busy(rule.id)}
                            onClick={() => setRemoving(row)}
                          >
                            {t('common:actions.remove')}
                          </HpButton>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns} className="bo-cell-empty">
                  {t('rules.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
      <ConfirmDialog
        open={removing !== null}
        title={t('rules.removeDialog.title')}
        confirmLabel={t('common:actions.remove')}
        danger
        busy={remove.isPending}
        onConfirm={() => {
          if (removing?.rule) remove.mutate({ id: removing.rule.id });
        }}
        onCancel={() => setRemoving(null)}
      >
        <p>{t('rules.removeDialog.body', { name: removing?.campaign.name ?? '' })}</p>
      </ConfirmDialog>
    </>
  );
}
